import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { Base_URL } from "../../actions/Base_URL";  
import bgImage from "assets/img/register_bg_2.png";

export default function OTPVerification({
  email: propEmail,
  onVerificationSuccess,
  onBackClick,
  title = "Email Verification",
  successMessage = "Email verified successfully, please Login!",
  redirectPath = null,
}) {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  const email = propEmail || location.state?.email || "";
  const source = location.state?.source || "";
  const customTitle = location.state?.title || title;
  const customSuccessMessage = location.state?.successMessage || successMessage;
  const customRedirectPath = location.state?.redirectPath || redirectPath;

  useEffect(() => {
    if (location.state?.error) {
      // toast.error(location.state.error);
    } else if (location.state?.message) {
      // toast.success(location.state.message);
    }
  }, [location.state]);

  useEffect(() => {
    if (email) {
      handleResendEmailOtp(true);
    }
  }, [location.state?.email, email]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleOtpChange = (index, value) => {
   
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
    }
    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1].focus(); 
    }
  };


  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus(); 
    }
  };
  async function handleResendEmailOtp(bypass = false) {
    if (resendTimer > 0 && !bypass) return;
    setIsResending(true);
    try {
      const token = localStorage.getItem("token");
      const apiUrl = `${Base_URL}/otp/emailotp`;
      const response = await axios.post(
        apiUrl,
        {
          email: email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        setResendTimer(30);
        toast.success("New OTP sent to your email!");
      } else {
        toast.error(response.data?.message || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsResending(false);
    }
  }

  async function verifyEmailOtp(e) {
    e.preventDefault();

    const sendOtp = otp.join("");
    if (sendOtp.length !== 4) {
      toast.error("Please enter a complete 4-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const apiUrl = `${Base_URL}/otp/verifyemail`;
      const response = await axios.post(
        apiUrl,
        {
          email: email,
          otp: sendOtp,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        toast.success(customSuccessMessage);

        if (onVerificationSuccess) {
          onVerificationSuccess(response.data);
        } else {
          setTimeout(() => {
            if (customRedirectPath) {
              navigate(customRedirectPath);
            } else if (source === "registration") {
              navigate("/auth/login");
            } else {
              navigate("/login");
            }
          }, 2000);
        }
      } else {
        toast.error(response.data?.message || "Email verification failed");
      }
    } catch (error) {
      let errorMsg = "Email verification failed";
      if (error.response) {
        errorMsg =
          error.response.data?.message ||
          `Verification error (${error.response.status})`;
        if (error.response.status === 400) {
          errorMsg = "Invalid OTP - please try again";
        } else if (error.response.status === 404) {
          errorMsg = "Email not found - please request a new OTP";
        }
      } else if (error.request) {
        errorMsg = "Network error - please check your connection";
      } else {
        errorMsg = error.message || "Error setting up verification request";
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Toaster position="bottom-left" reverseOrder={false} />
      <main>
        <section className="relative w-full sm:py-3 min-h-screen">
          <div
            className="relative w-full min-h-screen flex items-center justify-center"
            style={{
              backgroundImage: `url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center 17%",
              backgroundRepeat: "no-repeat",
              overflow: "hidden",
            }}
          >
            <div
              className="absolute top-0 w-full h-full bg-opacity-60"
              style={{
                zIndex: "-1",
              }}
            ></div>

            <div className="w-full lg:w-4/12 px-8">
              <div
                className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-lg bg-white border border-blueGray-400 shadow-2xl shadow-blueGray-500/40"
                style={{
                  minHeight: "400px",
                  background: "linear-gradient(145deg, #f0f0f0, #cacaca)",
                }}
              >
                <div className="flex-auto px-4 lg:px-10 py-10 pt-8">
                  <div className="text-blueGray-900 text-center mb-8 font-bold text-2xl">
                    {customTitle}
                  </div>

                  {location.state?.message && (
                    <div className="text-center mb-6">
                      <p className="text-sm text-blueGray-600">
                        {location.state.message}
                      </p>
                    </div>
                  )}

                  <form onSubmit={verifyEmailOtp} className="mt-6">
                    <p className="text-sm text-blueGray-600 text-center mb-6">
                      Enter the 4-digit verification code sent to {email}.
                    </p>
                    <div className="flex items-center justify-center gap-5 mb-6">
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          type="text"
                          ref={(el) => (inputRefs.current[i] = el)}
                          className="w-12 h-12 text-center text-2xl font-extrabold text-blueGray-800 bg-white border border-blueGray-300 rounded-lg shadow outline-none focus:ring"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, i)}
                        />
                      ))}
                    </div>
                    <div className="text-center mt-8">
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-blueGray-800 text-white active:bg-blueGray-600 text-sm font-bold uppercase px-6 py-3 rounded-lg shadow hover:shadow-lg outline-none focus:outline-none w-full ease-linear transition-all duration-150"
                      >
                        {loading ? "Verifying..." : "Verify OTP"}
                      </button>
                    </div>
                    <p className="text-sm text-center text-blueGray-800 mb-6 mt-8">
                      Didn't receive OTP?{" "}
                      <button
                        type="button"
                        onClick={handleResendEmailOtp}
                        disabled={resendTimer > 0 || isResending}
                        className={`${
                          resendTimer > 0 || isResending
                            ? "text-gray-500"
                            : "text-blueGray-800"
                        } font-semibold hover:underline focus:outline-none`}
                      >
                        {isResending
                          ? "Sending..."
                          : resendTimer > 0
                          ? `Resend in ${resendTimer}s`
                          : "Resend"}
                      </button>
                    </p>
                  </form>

                  {onBackClick && (
                    <div className="text-center mt-6">
                      <button
                        onClick={onBackClick}
                        className="text-blueGray-600 hover:text-blueGray-800 text-sm font-semibold"
                      >
                        ← Back
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
